import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface PhoneNumberCardProps {
  phoneNumber: {
    id: string;
    twilio_phone_number: string;
    display_name: string;
    is_active: boolean;
    county: string;
    unread_count: number;
    last_message_at: string | null;
    sms_settings: {
      sms_enabled: boolean;
    } | null;
  };
}

export function PhoneNumberCard({ phoneNumber }: PhoneNumberCardProps) {
  const formattedPhone = phoneNumber.twilio_phone_number.replace(
    /^\+?1?(\d{3})(\d{3})(\d{4})$/,
    '($1) $2-$3'
  );

  return (
    <Link to={`/admin/sms-phone-numbers/${phoneNumber.id}/conversations`}>
      <Card className="p-4 hover:shadow-lg transition-all cursor-pointer border-2 hover:border-primary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-lg text-foreground truncate">
                  {formattedPhone}
                </h3>
                {phoneNumber.unread_count > 0 && (
                  <Badge variant="destructive" className="rounded-full px-2 py-0.5 text-xs">
                    {phoneNumber.unread_count}
                  </Badge>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground font-medium mb-1">
                {phoneNumber.display_name}
              </p>
              
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="capitalize">{phoneNumber.county}</span>
                {phoneNumber.last_message_at && (
                  <>
                    <span>•</span>
                    <span>
                      Last: {formatDistanceToNow(new Date(phoneNumber.last_message_at), { addSuffix: true })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {phoneNumber.sms_settings?.sms_enabled ? (
              <MessageSquare className="h-5 w-5 text-green-500" />
            ) : (
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
            )}
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      </Card>
    </Link>
  );
}
